<script>
import { mapActions } from 'vuex';
import _ from 'lodash';
import { freightDeliveryFee } from '@/utils/deliveryFeeGrouping';
import {
  CENTS_PER_DOLLAR,
  DISCOUNT_PRICE_LIST_PRICE,
  DISCOUNT_PRICE_NURSE_PRICE,
  DISCOUNT_PRICE_DOCTOR_PRICE,
  DISCOUNT_PRICE_RRP,
  DISCOUNT_TYPE_PERCENTAGE,
} from '../constants/price';

export default {
  data() {
    return {
      itemDiscounts: {},
      discountsArray: [],
      applicableDiscounts: [],
      freeShippingArray: [],
      allBulkDiscounts: [],
      allFreeShipping: [],
      productDiscounts: [],
      clinicDiscounts: [],
      minimumRequirement: {},
      openingOrderSuppliers: [],
      discountToGroups: [],
      discountGroups: [],
      promo: [],
      applicablePromo: [],
      totalPromo: 0,
      cartSubTotal: 0,
      taxableSubTotal: 0,
      totalDiscounts: 0,
      // FRH-732: whether the 2% card surcharge still applies. Defaults to true so a
      // failed lookup keeps charging rather than silently giving the fee away. The
      // server decides, because this browser supplies the Stripe charge amount and
      // so cannot be trusted to work it out from its own clock.
      cardSurchargeActive: true,
      // FRH-732: whether the surcharge lookup above has resolved. created() being
      // async does not block Vue from mounting and rendering, so this alone narrows
      // the window where a customer could submit before the answer is known — it
      // does not close it. Checkouts that already gate their submit button should
      // also require this to be true; see org-owner/superAdmin for the pattern.
      surchargeStatusLoaded: false,
      // FRH-736: whether the payment efficiency benefit is currently rolled
      // out. Defaults to false — the opposite of the surcharge's fail-open
      // default, since a failed lookup here must not hand out a discount
      // that was never actually authorized.
      paymentEfficiencyBenefitActive: false,
      // The rate the server reports back alongside `active`, so checkout
      // doesn't hardcode a value that could drift from a since-changed
      // server config.
      paymentEfficiencyBenefitRate: 0,
      // Whether Payment Terms orders get the discount too (the server's own
      // flag); off unless the server says so. Temporary: remove together with
      // the payment-efficiency-benefit-payment-terms flag.
      paymentTermsBenefitActive: false,
      benefitStatusLoaded: false,
    };
  },
  async created() {
    // A failed-order link names the clinic; take it before the benefit lookup,
    // whose rate depends on the clinic.
    this.applyRouteClinic();
    // Resolve first, not last, to keep the pre-load window as small as possible.
    // The two lookups are independent, so run them concurrently rather than
    // doubling the pre-load window by awaiting one after the other.
    await Promise.all([
      this.fetchCardSurchargeStatus().then(() => {
        this.surchargeStatusLoaded = true;
      }),
      this.fetchPaymentEfficiencyBenefitStatus(),
    ]);
    await this.placeFailedOrder();
    await this.getStoreBulkDiscount();
    await this.getStoreFreeShipping();
    await this.getStoreDiscounts();
    await this.getRequirementMinimum();
    await this.fetchMinimumOrderSuppliers();
    await this.getStorePromo();
    const items = this.$store.state.eCommerce.cartItems;
    this.checkfreeShipping(items);
    this.applyBarrelDiscount(items);
  },
  computed: {
    cartItems() {
      return this.$store.state.eCommerce.cartItems.slice().reverse();
    },
    isLoggedInSuperAdmin() {
      return this.$store.state.AppActiveUser.userType === 'superAdmin';
    },
  },
  methods: {
    ...mapActions('ecommerce', [
      'getMinimumOrderSuppliers',
      'getRequirementMinimumOrder',
      'getCartItemsFromApi',
      'insertFailedOrders',
      'getCardSurchargeStatus',
      'getPaymentEfficiencyBenefitStatus',
    ]),
    ...mapActions('storeProducts', [
      'fetchStoreBulkDiscount',
      'fetchStorePromo',
      'fetchStoreFreeShipping',
      'fetchClinicDiscounts',
    ]),
    async fetchCardSurchargeStatus() {
      try {
        const { data } = await this.getCardSurchargeStatus();
        const active = data && data.data && data.data.active;
        // A malformed response must not silently read as "still active" — that
        // would look identical to a successful lookup while masking a real bug.
        if (typeof active !== 'boolean') throw new Error('Unexpected card surcharge status response');
        this.cardSurchargeActive = active;
      } catch (error) {
        // Leave the surcharge active: charging as we do today is the safe failure.
        console.error(`Fetching card surcharge status error: ${error}`);
      }
    },
    async fetchPaymentEfficiencyBenefitStatus() {
      // Only the latest lookup counts: switching clinic while one is in flight
      // must not let the older answer land last.
      this.benefitStatusRequest = (this.benefitStatusRequest || 0) + 1;
      const request = this.benefitStatusRequest;
      this.benefitStatusLoaded = false;
      try {
        const { data } = await this.getPaymentEfficiencyBenefitStatus(
          sessionStorage.getItem('doctorClinicSelectedClinic'),
        );
        if (request !== this.benefitStatusRequest) return;
        const active = data && data.data && data.data.active;
        const rate = data && data.data && data.data.rate;
        if (typeof active !== 'boolean') throw new Error('Unexpected payment efficiency benefit status response');
        this.paymentEfficiencyBenefitActive = active;
        this.paymentTermsBenefitActive = active && data.data.paymentTerms === true;
        // Reject NaN, negatives, and anything >= 1 (e.g. a rate mistakenly
        // expressed as a percentage like 1.6 instead of the fraction 0.016) —
        // typeof NaN === 'number' would otherwise let it through and propagate
        // into every downstream money calculation.
        this.paymentEfficiencyBenefitRate =
          typeof rate === 'number' && Number.isFinite(rate) && rate >= 0 && rate < 1 ? rate : 0;
      } catch (error) {
        if (request !== this.benefitStatusRequest) return;
        // Turn the benefit off, so a failed lookup after a clinic switch can't
        // keep the previous clinic's rate: not applying an unconfirmed discount
        // is the safe failure, unlike the surcharge's fail-open default.
        this.paymentEfficiencyBenefitActive = false;
        this.paymentEfficiencyBenefitRate = 0;
        this.paymentTermsBenefitActive = false;
        console.error(`Fetching payment efficiency benefit status error: ${error}`);
      } finally {
        if (request === this.benefitStatusRequest) this.benefitStatusLoaded = true;
      }
    },
    applyRouteClinic() {
      const { clinic } = this.$route.query;
      if (clinic) sessionStorage.setItem('doctorClinicSelectedClinic', clinic.toString());
    },
    async placeFailedOrder() {
      const {requestId} = this.$route.params;

      if(requestId) {
        await this.insertFailedOrdersToCart(requestId);
      }
    },
    async applyBarrelDiscount(cartItems) {
      this.appliedDiscounts = [];
      this.applicableDiscounts = [];
      const clinicId = sessionStorage.getItem('doctorClinicSelectedClinic');
      let discounts = this.allBulkDiscounts;

      for (const group of this.discountGroups) {
        const associatedDiscountIds = this.discountToGroups
          .filter(d => d.groupId === group._id)
          .map(d => d.discountId);

        if (associatedDiscountIds.length > 1) {
          const associatedDiscounts = this.allBulkDiscounts.filter(d => associatedDiscountIds.includes(d._id));
          let isDoubleBarrel = true;
          for (const discount of associatedDiscounts) {
            if (!this.isGroupDiscountApplicable(discount, cartItems, clinicId)) {
              isDoubleBarrel = false;
              break;
            }
          }

          if (!isDoubleBarrel) {
            discounts = discounts.filter(bulk =>
              !associatedDiscounts.some(discount => discount._id === bulk._id)
            );
          }
        } else {
          discounts = discounts.filter(bulk =>
            !associatedDiscountIds.some(discount => discount === bulk._id)
          );
        }
      }
      this.applyDiscountsToCart(cartItems, discounts);
    },

    isGroupDiscountApplicable(discount, cartItems, clinicId) {

      const productQuantities = {};

      for (const cartItem of cartItems) {
        const productId = cartItem.productStoreId;
        const quantity = cartItem.quantity;
        if (productQuantities[productId]) {
          productQuantities[productId] += quantity;
        } else {
          productQuantities[productId] = quantity;
        }
      }

      const discountProductIdSet = new Set(discount.products.map(product => product.productId));
      let totalDiscountQuantity = 0;

      for (const productId of discountProductIdSet) {
        if (productQuantities[productId]) {
          totalDiscountQuantity += productQuantities[productId];
        }
      }

      const minTotalQTY = this.getMinTotalQuantity(discount);
      let maxTotalQTY = this.getMaxTotalQuantity(discount);

      if(!maxTotalQTY || maxTotalQTY < 1) {
        maxTotalQTY = 1000000;
      }

      let clinicIsEligible = true;
      if(discount.clinic_is_equal) {
        clinicIsEligible = discount.clinics.some(c => c.clinicId === clinicId);
      } else if(discount.clinics.length) {
        clinicIsEligible = !discount.clinics.some(c => c.clinicId === clinicId);
      }

      return (
        clinicIsEligible && (totalDiscountQuantity >= minTotalQTY && totalDiscountQuantity <= maxTotalQTY)
      );
    },
    hasClinicDiscount(productStoreId) {
      return this.clinicDiscounts.some((i) => i.productDiscounts.some((ii) => ii.productStoreId === productStoreId && ii.isActive));
    },
    buildProductPricesMap(rule) {
      if (!rule._productPricesMap && rule.productPrices && Array.isArray(rule.productPrices)) {
        rule._productPricesMap = rule.productPrices.reduce((map, item) => {
          if (item.productId) {
            map[item.productId] = item;
          }
          return map;
        }, {});
      }
      return rule._productPricesMap || {};
    },
    getProductFreshPriceFromRule(rule, productId) {
      const pricesMap = this.buildProductPricesMap(rule);
      const productPrice = pricesMap[productId];
      
      if (productPrice && productPrice.fresh_price !== null && productPrice.fresh_price !== undefined) {
        return productPrice.fresh_price;
      }
      
      return rule.fresh_price || 0;
    },
    applyPromo(cartItems, productQuantities, discounts) {
      const clinicId = sessionStorage.getItem('doctorClinicSelectedClinic');
      let applicablePromo = [];
      let productIdsToBeDeleted = [];

      /**
       * Match promo based on min_qty & max_qty rules
       */
      for (const promo of this.promo) {
        const promoProductIdSet = new Set(promo.products.map(product => product.productId));
        const promoClinicIdSet = new Set(promo.clinics.map(clinic => clinic.clinicId));
        let totalPromoQties = 0;
        let promoProductIds = [];
        let matchPromoProduct = false;

        for (const productId of promoProductIdSet) {
          if (productQuantities[productId]) {
            matchPromoProduct = true;
            totalPromoQties += productQuantities[productId];
            promoProductIds.push(productId);
          }
        }

        // Check if the clinic is eligible
        let clinicIsEligible = true;
        if (promo.clinic_is_equal) {
          clinicIsEligible = promoClinicIdSet.has(clinicId);
        } else if (promo.clinics.length) {
          clinicIsEligible = !promoClinicIdSet.has(clinicId);
        }

        if (matchPromoProduct && clinicIsEligible) {
          const getHighestRuleReducer = (prev, curr) => {
            if (prev.fresh_price < curr.fresh_price) return prev;
            return curr;
          };
          const matchedRules = promo.discount_rules.filter((rule) => rule.min_qty <= totalPromoQties && rule.max_qty >= totalPromoQties);
          const highestRule = matchedRules.length ? matchedRules.reduce(getHighestRuleReducer) : undefined;

          if (highestRule) {
            let newProductIds = [];
            let totalPromo = 0;

            /**
             * Promo, clinic discount, bulk discount
             *
             * 1. has clinic discount but less
             * 2. no clinic discount
             * 3. has bulk discount but less
             * 4. no bulk discount
             */
            promoProductIds.map((id) => {
              const item = cartItems.find((i) => i.productStoreId === id);
              const freshPrice = this.getProductFreshPriceFromRule(highestRule, id) * CENTS_PER_DOLLAR;
              
              // Determine base price based on discount_price field
              let basePrice = item.price;
              if (highestRule.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
                basePrice = item.listPrice || item.price;
              } else if (highestRule.discount_price === DISCOUNT_PRICE_NURSE_PRICE) {
                basePrice = item.originalPrice || item.price;
              } else if (highestRule.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
                basePrice = item.doctorPrice || item.price;
              } else if (highestRule.discount_price === DISCOUNT_PRICE_RRP) {
                basePrice = item.rrp || item.price;
              }
              
              const promoSave = (basePrice - freshPrice) * item.quantity;
              const matchedDiscount = discounts.find((i) => i.productIds.includes(id));

              // has clinic discount & promo but promo < to clinic discount(no bulk discount)
              if (
                item && this.hasClinicDiscount(id) &&
                item.originalPrice &&
                basePrice > freshPrice &&
                !matchedDiscount
              ) {
                newProductIds.push(id);
                totalPromo += promoSave;
              }

              // has bulk discount & promo but promo < to bulk discount
              if (
                item &&
                matchedDiscount &&
                matchedDiscount.productsDiscounts[id] &&
                matchedDiscount.productsDiscounts[id] > freshPrice
              ) {
                productIdsToBeDeleted.push(id);
                newProductIds.push(id);
                totalPromo += promoSave;
              }

              // no clinic discount & no bulk discount
              if (
                item &&
                (!this.hasClinicDiscount(id) && !matchedDiscount)
              ) {
                newProductIds.push(id);
                totalPromo += promoSave;
              }
            });
            promoProductIds = newProductIds;

            // skip if no product ids
            if (!newProductIds.length) continue;

            // Check if has a similar promo with same product/s
            const existingPromoIndex = applicablePromo.findIndex(appliedPromo =>
              appliedPromo.productIds.some(productId =>
                promoProductIds.includes(productId)
              )
            );
            if (existingPromoIndex > -1) {
              const existingPromo = applicablePromo[existingPromoIndex];

              /**
               * Filter out product that are >
               * current promo
               */
              const excludedProductIds = existingPromo.productIds.filter((id) => {
                const existingFreshPrice = this.getProductFreshPriceFromRule(existingPromo.rule, id);
                const newFreshPrice = this.getProductFreshPriceFromRule(highestRule, id);
                return newFreshPrice < existingFreshPrice && promoProductIds.includes(id);
              });
              if (excludedProductIds.length) {
                // exclude those products that > current promo
                const remainingProductIds = existingPromo.productIds.filter((id) => !excludedProductIds.includes(id));

                // Re-calculate save promo for remaining products
                let updatedTotalPromo = 0;
                cartItems.map((i) => {
                  if (remainingProductIds.includes(i.productStoreId)) {
                    const freshPrice = this.getProductFreshPriceFromRule(existingPromo.rule, i.productStoreId) * CENTS_PER_DOLLAR;
                    
                    // Determine base price based on discount_price field
                    let basePrice = i.price;
                    if (existingPromo.rule.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
                      basePrice = i.listPrice || i.price;
                    } else if (existingPromo.rule.discount_price === DISCOUNT_PRICE_NURSE_PRICE) {
                      basePrice = i.originalPrice || i.price;
                    } else if (existingPromo.rule.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
                      basePrice = i.doctorPrice || i.price;
                    } else if (existingPromo.rule.discount_price === DISCOUNT_PRICE_RRP) {
                      basePrice = i.rrp || i.price;
                    }
                    
                    updatedTotalPromo += (basePrice - freshPrice) * i.quantity;
                  }
                });

                if (remainingProductIds.length) {
                  // update existing promo productIds
                  applicablePromo[existingPromoIndex] = {
                    ...existingPromo,
                    productIds: remainingProductIds,
                    totalPromo: updatedTotalPromo,
                  };
                } else {
                  // if no remaining product ids, delete
                  applicablePromo.splice(existingPromoIndex, 1);
                }

                // push new discount
                applicablePromo.push({
                  _id: promo._id,
                  promoName: promo.discount_name,
                  rule: {
                    ...highestRule,
                    fresh_price: highestRule.fresh_price ? highestRule.fresh_price * CENTS_PER_DOLLAR : null,
                  },
                  productIds: promoProductIds,
                  totalPromo,
                });
              }
            } else {
              applicablePromo.push({
                _id: promo._id,
                promoName: promo.discount_name,
                rule: {
                  ...highestRule,
                  fresh_price: highestRule.fresh_price ? highestRule.fresh_price * 100 : null,
                },
                productIds: promoProductIds,
                totalPromo,
              });
            }
          }
        }
      }

      productIdsToBeDeleted.map((id) => {
        const matchedIndex = discounts.findIndex((i) => i.productIds.includes(id));

        if (matchedIndex !== -1) {
          let currentDiscount = discounts[matchedIndex];
          const matchProductIndex = currentDiscount.productIds.findIndex((i) => i === id);

          // delete product
          currentDiscount.productIds.splice(matchProductIndex, 1);

          if (currentDiscount.productIds.length) {
            let updatedQties = 0;

            // update totalDiscountQuantity
            currentDiscount.productIds.map((i) => {
              const matchCartItemIndex = cartItems.findIndex(({ productStoreId }) => productStoreId === i);
              if (matchCartItemIndex !== -1) {
                updatedQties += cartItems[matchCartItemIndex].quantity;
              }
            });

            // Calculate the total discount based on discount value and total quantity
            let totalDiscount = currentDiscount.rule.discount_value;
            if (currentDiscount.rule.discount_type === 'percentage') {
              totalDiscount = this.calculateTotalDiscount(cartItems, currentDiscount.productIds, currentDiscount.rule);
            } else {
              totalDiscount = totalDiscount * CENTS_PER_DOLLAR;
            }

            currentDiscount.totalDiscountQuantity = updatedQties;
            currentDiscount.totalDiscount = totalDiscount;

            discounts[matchedIndex] = currentDiscount;
          } else {
            discounts.splice(matchedIndex, 1);
          }
        }
      });

      return {
        applicablePromo,
        discounts,
      };
    },
    async applyDiscountsToCart(cartItems, discounts) {
      let appliedDiscounts = [];
      const clinicId = sessionStorage.getItem('doctorClinicSelectedClinic');
      const productQuantities = {};  // To store total quantities of each product
      const productPrice = {};  // To store total quantities of each product
      const productListPrice = {};  // To store total quantities of each product

      // Calculate total quantities of each product in the cart
      for (const cartItem of cartItems) {
        const productId = cartItem.productStoreId;
        const quantity = cartItem.quantity;
        let price = cartItem.price;
        let listPrice = cartItem.listPrice || 0;

        // if has clinic discount, use originalPrice as price to compute total price
        if (this.hasClinicDiscount(productId) && cartItem.originalPrice) price = cartItem.originalPrice;

        if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminPrice')) price = cartItem.superAdminPrice;
        if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminDiscountPrice')) listPrice = cartItem.superAdminDiscountPrice;

        if (productQuantities[productId]) {
          productQuantities[productId] += quantity;
          productPrice[productId] += price * quantity;
          productListPrice[productId] += listPrice * quantity;
        } else {
          productQuantities[productId] = quantity;
          productPrice[productId] = price * quantity;
          productListPrice[productId] = listPrice * quantity;
        }
      }

      // Apply discounts based on total product quantities
      for (const discount of discounts) {
        const discountProductIdSet = new Set(discount.products.map(product => product.productId));
        const discountClinicIdSet = new Set(discount.clinics.map(clinic => clinic.clinicId));
        let totalDiscountQuantity = 0;
        let totalPrice = 0;
        let totalListPrice = 0;
        let discountProductIds = [];

        // Calculate total quantity of products matching the discount
        for (const productId of discountProductIdSet) {
          if (productQuantities[productId]) {
            totalDiscountQuantity += productQuantities[productId];
            totalPrice += productPrice[productId];
            totalListPrice += productListPrice[productId];
            discountProductIds.push(productId);
          }
        }

        // Check if the clinic is eligible and the total quantity meets the discount rule
        let clinicIsEligible = true;
        if (discount.clinic_is_equal) {
          clinicIsEligible = discountClinicIdSet.has(clinicId);
        } else if (discount.clinics.length) {
          clinicIsEligible = !discountClinicIdSet.has(clinicId);
        }

        const minTotalQTY = this.getMinTotalQuantity(discount);
        let maxTotalQTY = this.getMaxTotalQuantity(discount);

        if (!maxTotalQTY || maxTotalQTY < 1) {
          maxTotalQTY = 1000000;
        }

        if (clinicIsEligible && (totalDiscountQuantity >= minTotalQTY && totalDiscountQuantity <= maxTotalQTY)) {
          const highestDiscountRule = this.getHighestDiscountRule(discount.discount_rules, totalDiscountQuantity, totalPrice, totalListPrice);
          if (highestDiscountRule) {
            // reset total qty to 0 to recalculate clinic discount & bulk discount
            totalDiscountQuantity = 0;
            let newProductIds = [];
            const productsDiscounts = {};

            // bulk discount x clinic discount
            discountProductIds.map((id) => {
              const item = cartItems.find((i) => i.productStoreId === id);
              const discountPrice = this.discountedPrice(item, highestDiscountRule);

              // has clinic discount & bulk discount < to clinic discount
              if (item && this.hasClinicDiscount(id) && item.originalPrice && item.price > discountPrice) {
                newProductIds.push(id);
                totalDiscountQuantity += item.quantity;
                productsDiscounts[id] = discountPrice;
              }

              // no clinic discount
              if (!this.hasClinicDiscount(id)) {
                newProductIds.push(id);
                totalDiscountQuantity += item.quantity;
                productsDiscounts[id] = discountPrice;
              }
            });
            discountProductIds = newProductIds;

            // skip if no product ids
            if (!newProductIds.length) continue;

            // Calculate the total discount based on discount value and total quantity
            let totalDiscount = highestDiscountRule.discount_value;
            if (highestDiscountRule.discount_type === 'percentage') {
              totalDiscount = this.calculateTotalDiscount(cartItems, discountProductIds, highestDiscountRule);
            } else {
              totalDiscount = totalDiscount * CENTS_PER_DOLLAR;
            }

            // Check if a similar discount is already applied
            const existingDiscountIndex = appliedDiscounts.findIndex(appliedDiscount =>
              appliedDiscount.productIds.some(productId =>
                discountProductIds.includes(productId)
              )
            );

            if (existingDiscountIndex > -1) {
              const existingDiscount = appliedDiscounts[existingDiscountIndex];
              const sameProductIds = existingDiscount.productIds.filter((id) => {
                const item = cartItems.find((i) => i.productStoreId === id);
                const prevDiscountPrice = this.discountedPrice(item, existingDiscount.rule);
                const currDiscountPrice = this.discountedPrice(item, highestDiscountRule);

                return discountProductIds.includes(id) && currDiscountPrice < prevDiscountPrice;
              });

              // if same current discount products is > prev discount products
              if (sameProductIds.length) {
                let remainingTotalQty = 0;
                const updatedProductDiscounts = {};
                const remainingProductIds = existingDiscount.productIds.filter((id) => !sameProductIds.includes(id));

                // Re-calculate total qty for remaining products
                cartItems.map((i) => {
                  if (remainingProductIds.includes(i.productStoreId)) {
                    remainingTotalQty += i.quantity;
                    updatedProductDiscounts[i.productStoreId] = existingDiscount.productsDiscounts[i.productStoreId];
                  }
                });
                // Re-calculate the total discount based on discount value and total quantity
                let updatedTotalDiscount = existingDiscount.rule.discount_value * CENTS_PER_DOLLAR;
                if (highestDiscountRule.discount_type === 'percentage') {
                  updatedTotalDiscount = this.calculateTotalDiscount(cartItems, remainingProductIds, existingDiscount.rule);
                }

                if (remainingProductIds.length) {
                  // update existing discount
                  appliedDiscounts[existingDiscountIndex] = {
                    ...existingDiscount,
                    totalDiscountQuantity: remainingTotalQty,
                    productIds: remainingProductIds,
                    totalDiscount: updatedTotalDiscount,
                    productsDiscounts: updatedProductDiscounts,
                  };
                } else {
                  // if no remaining product ids, delete
                  appliedDiscounts.splice(existingDiscountIndex, 1);
                }

                // push new discount
                appliedDiscounts.push({
                  discountId: discount._id,
                  totalDiscountQuantity,
                  rule: highestDiscountRule,
                  productIds: discountProductIds,
                  totalDiscount,
                  groupId: discount.groupId,
                  productsDiscounts,
                });
              }
            } else {
              // If no similar discount is applied, add the new discount
              appliedDiscounts.push({
                discountId: discount._id,
                totalDiscountQuantity,
                rule: highestDiscountRule,
                productIds: discountProductIds,
                totalDiscount,
                groupId: discount.groupId,
                productsDiscounts,
              });
            }
          }
        }
      }

      const {
        applicablePromo,
        discounts: updatedAppliedDiscounts,
      } = this.applyPromo(cartItems, productQuantities, appliedDiscounts);

      appliedDiscounts = updatedAppliedDiscounts;

      const totalPromo = applicablePromo.reduce((total, accu) => {
        total += accu.totalPromo;
        return total;
      }, 0);

      // Add the total discount to the running total
      const totalDiscounts = appliedDiscounts.reduce((total, val)=>{
        total += val.totalDiscount;
        return total;
      }, 0);

      // Calculate the cart subtotal after applying discounts
      let subtotal = 0;
      let isTaxable = 0;
      for (const cartItem of cartItems) {
        // Use the appropriate price based on the discount rule
        const discountRule = appliedDiscounts.find(appliedDiscount =>
          appliedDiscount.productIds.includes(cartItem.productStoreId)
        );
        // Check for promo/tier discount
        const promoRule = applicablePromo.find(promo =>
          promo.productIds.includes(cartItem.productStoreId)
        );
        let price = cartItem.price;

        // has bulk discount rule & clinic discount, bulk discount < clinic discount
        if (discountRule && this.hasClinicDiscount(cartItem.productStoreId) && cartItem.originalPrice) price = cartItem.originalPrice;

        if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminPrice')) price = cartItem.superAdminPrice;

        // For tiered costing with fresh_price, use that directly (check this first)
        if (discountRule && discountRule.rule.fresh_price != null) {
          const freshPrice = this.getProductFreshPriceFromRule(discountRule.rule, cartItem.productStoreId);
          price = freshPrice * CENTS_PER_DOLLAR;
        } else if (discountRule) {
          const rule = discountRule.rule;
          
          // Determine base price source
          if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminDiscountPrice')) {
            price = cartItem.superAdminDiscountPrice;
          } else if (rule.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
            price = cartItem.listPrice;
          } else if (rule.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
            price = cartItem.doctorPrice;
          } else if (rule.discount_price === DISCOUNT_PRICE_NURSE_PRICE && cartItem.originalPrice) {
            price = cartItem.originalPrice;
          }

          // Apply discount calculations
          if (rule.discount_type === DISCOUNT_TYPE_PERCENTAGE) {
            price = price - (price * (rule.discount_value / CENTS_PER_DOLLAR));
          } else if (rule.itemDiscount) {
            price = price - (rule.itemDiscount * CENTS_PER_DOLLAR);
          }
        }

        // Apply promo/tier discount price if available (overrides discount price)
        if (promoRule && promoRule.rule) {
          // For tier cost rules, calculate fresh_price using getProductFreshPriceFromRule
          // This handles both productPrices (per-product) and global fresh_price
          const freshPriceInDollars = this.getProductFreshPriceFromRule(promoRule.rule, cartItem.productStoreId);
          if (freshPriceInDollars && freshPriceInDollars > 0) {
            // Convert from dollars to cents
            price = freshPriceInDollars * CENTS_PER_DOLLAR;
          }
        }

        if(cartItem.isTaxable) {
          // For taxable items, use the final price (after all discounts/promos) for GST calculation
          isTaxable += price * cartItem.quantity;
        }

        // For subtotal calculation: use the original price before discounts
        // Similar to how tier discount works, determine original price based on discount_price field
        let subtotalPrice = cartItem.price;
        
        // Determine the rule to use for determining original price
        // Prioritize promo rule over discount rule since promo overrides discount
        const rule = (promoRule && promoRule.rule) || (discountRule && discountRule.rule);
        const fallbackRule = (promoRule && promoRule.rule) ? (discountRule && discountRule.rule) : null;
        const ruleToUse = (rule && rule.discount_price) ? rule : (fallbackRule && fallbackRule.discount_price) ? fallbackRule : null;
        
        if (ruleToUse && ruleToUse.discount_price) {
          // Use original price based on discount_price field (similar to tier discount logic)
          if (ruleToUse.discount_price === DISCOUNT_PRICE_RRP) {
            subtotalPrice = cartItem.rrp;
          } else if (ruleToUse.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
            subtotalPrice = cartItem.listPrice;
          } else if (ruleToUse.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
            subtotalPrice = cartItem.doctorPrice;
          } else if (ruleToUse.discount_price === DISCOUNT_PRICE_NURSE_PRICE && cartItem.originalPrice) {
            subtotalPrice = cartItem.originalPrice;
          } else {
            // Default to originalPrice or price if discount_price is not recognized
            subtotalPrice = cartItem.originalPrice || cartItem.price;
          }
        } else if (discountRule && discountRule.rule.discount_price) {
          // Fallback: use discount rule's discount_price if no ruleToUse
          if (discountRule.rule.discount_price === DISCOUNT_PRICE_RRP) {
            subtotalPrice = cartItem.rrp;
          } else if (discountRule.rule.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
            subtotalPrice = cartItem.listPrice;
          } else if (discountRule.rule.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
            subtotalPrice = cartItem.doctorPrice;
          } else if (discountRule.rule.discount_price === DISCOUNT_PRICE_NURSE_PRICE && cartItem.originalPrice) {
            subtotalPrice = cartItem.originalPrice;
          } else {
            subtotalPrice = cartItem.originalPrice || cartItem.price;
          }
        } else {
          // No discount rule, use original price or default price
          subtotalPrice = cartItem.originalPrice || cartItem.price;
        }
        
        // Handle superAdmin case
        if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminPrice')) {
          subtotalPrice = cartItem.superAdminPrice;
        } else if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminDiscountPrice') && ruleToUse && ruleToUse.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
          subtotalPrice = cartItem.superAdminDiscountPrice;
        }
        
        subtotal += subtotalPrice * cartItem.quantity;

      }

      // filter if there are minimum discount purchase applicable
      const filteredDiscounts = [];
      // Grouping the discounts by groupId
      const groupedDiscounts = appliedDiscounts.reduce((acc, discount) => {
        const groupId = discount.groupId;

        if (!groupId) {
          // If groupId is missing, push it right away
          filteredDiscounts.push(discount);
        } else {
          // Group by groupId
          acc[groupId] = acc[groupId] || [];
          acc[groupId].push(discount);
        }

        return acc;
      }, {});

      // Iterate over the grouped discounts and push to the filteredDiscounts array
      for (const groupId in groupedDiscounts) {
        if (groupedDiscounts[groupId].length > 1) {
          // If there are multiple discounts with the same groupId, push all of them
          filteredDiscounts.push(...groupedDiscounts[groupId]);
        }
      }


      // Include total discounts in the response
      this.applicableDiscounts = filteredDiscounts;
      this.cartSubTotal = subtotal;
      this.taxableSubTotal = isTaxable;
      this.totalDiscounts = totalDiscounts;
      this.applicablePromo = applicablePromo;
      this.totalPromo = totalPromo;
    },
    calculateDiscountPerCartItem(cartItem, discountRule) {
      let totalDiscount = 0;
      let basePrice = cartItem.price;

      // Determine base price based on discount_price field
      if (discountRule.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
        basePrice = cartItem.listPrice || cartItem.price;
      } else if (discountRule.discount_price === DISCOUNT_PRICE_NURSE_PRICE) {
        basePrice = cartItem.originalPrice || cartItem.price;
      } else if (discountRule.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
        basePrice = cartItem.doctorPrice || cartItem.price;
      } else if (discountRule.discount_price === DISCOUNT_PRICE_RRP) {
        basePrice = cartItem.rrp || cartItem.price;
      }

      // if superAdminDiscountPrice exists, override discount price
      if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminDiscountPrice')) {
        basePrice = cartItem.superAdminDiscountPrice;
      }

      // Calculate discount based on discount_type
      if (discountRule.discount_type === DISCOUNT_TYPE_PERCENTAGE) {
        totalDiscount = (basePrice * cartItem.quantity * discountRule.discount_value) / CENTS_PER_DOLLAR;
      } else if (discountRule.itemDiscount) {
        // itemDiscount is in dollars, convert to cents
        totalDiscount = discountRule.itemDiscount * cartItem.quantity * CENTS_PER_DOLLAR;
      }

      return totalDiscount;
    },
    calculateTotalDiscount(cartItems, discountProductIds, discountRule) {
      let totalDiscount = 0;
      for (const cartItem of cartItems) {
        if (discountProductIds.includes(cartItem.productStoreId)) {
          totalDiscount += this.calculateDiscountPerCartItem(cartItem, discountRule);
        }
      }
      return totalDiscount;
    },
    calculateTotalDiscountWithTax(cartItems, discountProductIds, discountRule) {
      let totalDiscount = 0;
      for (const cartItem of cartItems) {
        if (discountProductIds.includes(cartItem.productStoreId) && cartItem.isTaxable) {
          let basePrice = cartItem.price;

          // Determine base price based on discount_price field
          if (discountRule.discount_price === DISCOUNT_PRICE_LIST_PRICE) {
            basePrice = cartItem.listPrice || cartItem.price;
          } else if (discountRule.discount_price === DISCOUNT_PRICE_NURSE_PRICE) {
            basePrice = cartItem.originalPrice || cartItem.price;
          } else if (discountRule.discount_price === DISCOUNT_PRICE_DOCTOR_PRICE) {
            basePrice = cartItem.doctorPrice || cartItem.price;
          } else if (discountRule.discount_price === DISCOUNT_PRICE_RRP) {
            basePrice = cartItem.rrp || cartItem.price;
          }

          // if superAdminDiscountPrice exists, override discount price
          if (this.isLoggedInSuperAdmin && cartItem.hasOwnProperty('superAdminDiscountPrice')) {
            basePrice = cartItem.superAdminDiscountPrice;
          }

          // Calculate discount based on discount_type
          if (discountRule.discount_type === DISCOUNT_TYPE_PERCENTAGE) {
            totalDiscount += (basePrice * cartItem.quantity * discountRule.discount_value) / CENTS_PER_DOLLAR;
          } else if (discountRule.itemDiscount) {
            // itemDiscount is in dollars, convert to cents
            totalDiscount += discountRule.itemDiscount * cartItem.quantity * CENTS_PER_DOLLAR;
          }
        }
      }
      return totalDiscount;
    },
    getMinTotalQuantity(discount) {
      return Math.min(...discount.discount_rules.map(rule => rule.min_qty));
    },
    getMaxTotalQuantity(discount) {
      return Math.max(...discount.discount_rules.map(rule => rule.max_qty));
    },
    getHighestDiscountRule(discountRules, quantity, price, listPrice) {
      let highestDiscountRule = null;

      for (const discountRule of discountRules) {
        const minQTY = discountRule.min_qty;
        let maxQTY = discountRule.max_qty;  
        const minAmount = (discountRule.minimum_purchase_amount ? discountRule.minimum_purchase_amount : 0) * CENTS_PER_DOLLAR;

        if(!maxQTY || maxQTY < 1) {
          maxQTY = 1000000;
        }

        if(discountRule.discount_price === DISCOUNT_PRICE_LIST_PRICE && minAmount > 0 && minAmount <= listPrice) {
          highestDiscountRule = discountRule;
        } else if([DISCOUNT_PRICE_NURSE_PRICE, DISCOUNT_PRICE_DOCTOR_PRICE].includes(discountRule.discount_price) && minAmount > 0 && minAmount <= price) {
          highestDiscountRule = discountRule;
        } else if(!highestDiscountRule && minAmount > 0) {
          highestDiscountRule = null;
        } else if (!highestDiscountRule && minQTY <= quantity && maxQTY>= quantity) {
          if (!highestDiscountRule || discountRule.discount_value > highestDiscountRule.discount_value) {
            highestDiscountRule = discountRule;
          }
        }
      }

      if(highestDiscountRule) {
        return {
          ...highestDiscountRule,
          itemDiscount: highestDiscountRule.discount_type === 'fix' ? highestDiscountRule.discount_value/quantity : highestDiscountRule.discount_value,
        };
      }
      return null;
    },
    checkfreeShipping(cartItems) {
      const currentClincid = sessionStorage.getItem(
        'doctorClinicSelectedClinic'
      );
      const freeShippings = _.pullAll(
        _.cloneDeep(this.allFreeShipping).map((freeShipping) => {
          const productIds = freeShipping.products.map(
            (item) => item.productId
          );
          const clinicsIds = freeShipping.clinics.map((item) => item.clinicId);
          const produuctsInCurrentLimitation = _.pullAll(
            cartItems.map((product) => {
              if (_.indexOf(productIds, product.productStoreId) >= 0) {
                return product;
              }
              return undefined;
            }),
            [undefined]
          );

          const isForcurrentClinic =
            clinicsIds.length > 0
              ? _.indexOf(clinicsIds, currentClincid) >= 0
              : true;
          if (isForcurrentClinic && produuctsInCurrentLimitation.length > 0) {
            const productQuantity = _.sum(
              produuctsInCurrentLimitation.map((product) => product.quantity)
            );
            const isBelowMax =
              !freeShipping.max_qty || freeShipping.max_qty === 0
                ? true
                : productQuantity <= freeShipping.max_qty;
            const isAboveMin = productQuantity >= freeShipping.min_qty;
            if (isBelowMax && isAboveMin) {
              // Free shipping covers freight only, never the supplier's stacked delivery fee.
              const freightFee = freightDeliveryFee(produuctsInCurrentLimitation[0]);
              if (freeShipping.discount_type === DISCOUNT_TYPE_PERCENTAGE) {
                  freeShipping.totalDiscount =
                  (freightFee *
                  freeShipping.discount_value)/CENTS_PER_DOLLAR;
              } else if (
                freeShipping.discount_type === 'fix' &&
                freightFee > 0
              ) {
                freeShipping.totalDiscount = freeShipping.discount_value * CENTS_PER_DOLLAR;
              } else {
                freeShipping.totalDiscount = 0;
              }

              return {
                ...freeShipping,
              };
            }
            return undefined;
          }
          return undefined;
        }),
        [undefined]
      );
      this.freeShippingArray = freeShippings;
    },
    async insertFailedOrdersToCart(requestId) {
      const clinicId = sessionStorage.getItem('doctorClinicSelectedClinic');
      await this.insertFailedOrders({requestId, clinicId});
      await this.getCartItemsFromApi();
    },
    async getStoreBulkDiscount() {
      const { data } = await this.fetchStoreBulkDiscount();
      this.allBulkDiscounts = data.data.discounts.docs;
      this.discountGroups = data.data.groups;
      this.discountToGroups = data.data.discountGroups;
    },
    async getStoreFreeShipping() {
      const { data } = await this.fetchStoreFreeShipping();
      this.allFreeShipping = data.data.docs;
    },
    async getStorePromo() {
      try {
        const { data } = await this.fetchStorePromo();
        this.promo = data.data.docs;
      } catch (err) {
        console.log('Error fetching store promo::', err.message);
      }
    },
    async getStoreDiscounts() {
      try {
        const currentClincid = sessionStorage.getItem(
          'doctorClinicSelectedClinic'
        );
        const { data } = await this.fetchClinicDiscounts(currentClincid);
        this.clinicDiscounts = data.data;
      } catch (error) {
        console.log('error fetching product discount');
      }
    },
    async getRequirementMinimum() {
      try {
        const { data } = await this.getRequirementMinimumOrder();
        this.minimumRequirement = data.data;
      } catch (error) {
        console.log(`Fetching requirement order erorr: ${error}`);
      }
    },
    async fetchMinimumOrderSuppliers() {
      try {
        const { data } = await this.getMinimumOrderSuppliers();
        if(data && data.data && data.data.value) {
          this.openingOrderSuppliers = JSON.parse(data.data.value);
        }
      } catch (error) {
        console.log(`Fetching list of supplier minimum order erorr: ${error}`);
      }
    },
    discountedPrice(item, rule) {
      // For tiered costing with fresh_price, use that directly
      if (rule.fresh_price != null) {
        const freshPrice = this.getProductFreshPriceFromRule(rule, item.productStoreId);
        return freshPrice * CENTS_PER_DOLLAR;
      }
      // For regular discounts (percentage or fix)
      let price = item.price;

      if (this.hasClinicDiscount(item.productStoreId) && item.originalPrice) price = item.originalPrice;

      // discount is List Price, use listPrice
      if (rule.discount_price && rule.discount_price === DISCOUNT_PRICE_LIST_PRICE) price = item.listPrice;

      // if key superAdminDiscountPrice exists, override discount price
      if (item.hasOwnProperty('superAdminDiscountPrice')) price = item.superAdminDiscountPrice * CENTS_PER_DOLLAR;

      if (rule.discount_price && rule.discount_price === DISCOUNT_PRICE_LIST_PRICE && rule.discount_type === DISCOUNT_TYPE_PERCENTAGE) {
        return price - (price * (rule.discount_value/CENTS_PER_DOLLAR));
      } else if(rule.discount_price && rule.discount_price === DISCOUNT_PRICE_LIST_PRICE && rule.discount_type === 'fix') {
        return price - (rule.itemDiscount * CENTS_PER_DOLLAR);
      } else if(rule.discount_type === DISCOUNT_TYPE_PERCENTAGE) {
        return price - (price * (rule.discount_value/CENTS_PER_DOLLAR));
      }

      return price - (rule.itemDiscount * 100);
    },
  },
  watch: {
    '$store.state.eCommerce.cartItems': function (val) {
      this.applyBarrelDiscount(val);
    },
    // Key accounts get a higher rate, so switching clinic needs a fresh answer.
    '$store.state.clinicId': function () {
      this.fetchPaymentEfficiencyBenefitStatus();
    },
    applicableDiscounts: function(newval,oldval) {
      if(newval !== oldval) this.discountsArray = newval;
    },
  },
};
</script>
